import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ShoppingBag, ArrowRight, Ruler, Heart, Star } from "lucide-react";
import { fetchProducts, type ShopifyProduct, getOptimizedImageUrl } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { Navigation } from "@/components/Navigation";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LazyImage } from "@/components/LazyImage";

const Index = () => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const addItem = useCartStore(state => state.addItem);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const data = await fetchProducts(12);
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error("Failed to load products");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCart = (product: ShopifyProduct) => {
    const variant = product.node.variants.edges[0].node;
    addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions
    });
    toast.success("Added to cart");
  };

  // JSON-LD structured data
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "AI Press-On Nails",
    "url": "https://nailspresson.lovable.app",
    "description": "Custom AI-designed press-on nails. Create unique nail art with artificial intelligence.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://nailspresson.lovable.app/?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "AI Press-On Nails",
    "url": "https://nailspresson.lovable.app",
    "logo": "https://nailspresson.lovable.app/favicon.ico",
    "sameAs": [
      "https://www.instagram.com/nailspresson",
      "https://www.tiktok.com/@nailspresson",
      "https://www.pinterest.com/nailspresson",
      "https://www.facebook.com/nailspresson"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "nailspresson.contact@gmail.com",
      "contactType": "customer service"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "GB"
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "AI Press-On Nails",
    "image": "https://nailspresson.lovable.app/og-image.jpg",
    "url": "https://nailspresson.lovable.app",
    "telephone": "",
    "email": "nailspresson.contact@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "London",
      "addressRegion": "Greater London",
      "addressCountry": "GB"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 51.5074,
      "longitude": -0.1278
    },
    "priceRange": "££",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday"],
        "opens": "10:00",
        "closes": "16:00"
      }
    ],
    "areaServed": [
      {
        "@type": "Country",
        "name": "United Kingdom"
      },
      {
        "@type": "Country",
        "name": "Ireland"
      }
    ],
    "serviceType": ["Press-On Nails", "Custom Nail Art", "AI Nail Design"],
    "sameAs": [
      "https://www.instagram.com/nailspresson",
      "https://www.tiktok.com/@nailspresson",
      "https://www.pinterest.com/nailspresson",
      "https://www.facebook.com/nailspresson"
    ]
  };

  // BreadcrumbList Schema for navigation structure
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://nailspresson.lovable.app/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Shop",
        "item": "https://nailspresson.lovable.app/#products"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "AI Design",
        "item": "https://nailspresson.lovable.app/ai-design"
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "Size Chart",
        "item": "https://nailspresson.lovable.app/size-chart"
      }
    ]
  };

  // ItemList Schema for product carousel rich snippets
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Press-On Nail Collections",
    "description": "Browse our collection of AI-designed custom press-on nails",
    "numberOfItems": products.length,
    "itemListElement": products.slice(0, 8).map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.node.title,
        "description": product.node.description || `Premium press-on nails - ${product.node.title}`,
        "url": `https://nailspresson.lovable.app/product/${product.node.handle}`,
        "image": product.node.images.edges[0]?.node.url || "https://nailspresson.lovable.app/og-image.jpg",
        "offers": {
          "@type": "Offer",
          "price": parseFloat(product.node.priceRange.minVariantPrice.amount).toFixed(2),
          "priceCurrency": product.node.priceRange.minVariantPrice.currencyCode,
          "availability": "https://schema.org/InStock",
          "seller": {
            "@type": "Organization",
            "name": "AI Press-On Nails UK"
          }
        }
      }
    }))
  };

  // FAQ Schema for rich snippets
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How long do press-on nails last?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Press-on nails typically last 1-2 weeks with proper application. Using nail glue instead of adhesive tabs can extend wear time. Avoid excessive water exposure and apply cuticle oil daily for best results."
        }
      },
      {
        "@type": "Question",
        "name": "Are press-on nails better than acrylics?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Press-on nails are gentler on natural nails as they don't require harsh chemicals or drilling. They're more affordable, reusable, and can be applied at home in minutes. Unlike acrylics, they won't damage your nail bed when removed properly."
        }
      },
      {
        "@type": "Question",
        "name": "How do I find my press-on nail size?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Measure the widest part of your natural nail from edge to edge. Our size chart provides measurements in millimetres for each nail size. Most sets include sizes 0-9 to fit all finger widths. When between sizes, choose the smaller size for a better fit."
        }
      },
      {
        "@type": "Question",
        "name": "Can I reuse press-on nails?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! Quality press-on nails can be reused multiple times. After removal, gently clean off any remaining adhesive, store them in the original packaging, and reapply with fresh glue or adhesive tabs."
        }
      },
      {
        "@type": "Question",
        "name": "How do I remove press-on nails without damage?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Soak your nails in warm soapy water for 10-15 minutes to loosen the adhesive. Gently wiggle each nail from side to side - never pull or pry. For stubborn nails, apply cuticle oil around the edges and continue soaking."
        }
      },
      {
        "@type": "Question",
        "name": "What makes AI-designed nails unique?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our AI technology creates truly one-of-a-kind nail designs based on your preferences. Simply describe your vision, and our AI generates custom artwork that's never been made before - ensuring your nails are as unique as you are."
        }
      }
    ]
  };

  // HowTo Schema for application instructions
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Apply Press-On Nails",
    "description": "Complete guide to applying press-on nails at home for a salon-quality manicure that lasts up to 2 weeks.",
    "totalTime": "PT15M",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "GBP",
      "value": "15-35"
    },
    "supply": [
      { "@type": "HowToSupply", "name": "Press-on nail set" },
      { "@type": "HowToSupply", "name": "Nail glue or adhesive tabs" },
      { "@type": "HowToSupply", "name": "Nail file" },
      { "@type": "HowToSupply", "name": "Cuticle pusher" },
      { "@type": "HowToSupply", "name": "Alcohol wipe or nail dehydrator" }
    ],
    "tool": [
      { "@type": "HowToTool", "name": "Nail buffer (optional)" }
    ],
    "step": [
      {
        "@type": "HowToStep",
        "position": 1,
        "name": "Prepare your natural nails",
        "text": "Push back cuticles gently with a cuticle pusher. File your natural nails short and buff the surface lightly to remove shine. This helps the adhesive bond better."
      },
      {
        "@type": "HowToStep",
        "position": 2,
        "name": "Clean and dehydrate",
        "text": "Wipe each nail with an alcohol pad or nail dehydrator to remove any oils. Avoid touching your nails after this step to keep them clean."
      },
      {
        "@type": "HowToStep",
        "position": 3,
        "name": "Size your nails",
        "text": "Match each press-on nail to your natural nail width. The press-on should fit from sidewall to sidewall without overlapping the skin. Lay out all 10 nails in order before applying."
      },
      {
        "@type": "HowToStep",
        "position": 4,
        "name": "Apply adhesive",
        "text": "Apply a thin layer of nail glue to your natural nail and the back of the press-on, or use an adhesive tab on your natural nail. For longer wear, use glue instead of tabs."
      },
      {
        "@type": "HowToStep",
        "position": 5,
        "name": "Apply the press-on nail",
        "text": "Place the press-on at a 45-degree angle near your cuticle, then press down and forward to push out air bubbles. Hold firmly for 30 seconds with even pressure."
      },
      {
        "@type": "HowToStep",
        "position": 6,
        "name": "Secure and finish",
        "text": "Repeat for all nails, working from pinky to thumb. Avoid water for at least one hour after application. Apply cuticle oil around edges daily to extend wear time."
      }
    ]
  };

  // AggregateOffer Schema for price range across all products
  const aggregateOfferSchema = products.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Press-On Nails Collection",
    "description": "Premium AI-designed press-on nails. Custom nail art with unique designs, easy application, and salon-quality results.",
    "brand": {
      "@type": "Brand",
      "name": "AI Press-On Nails"
    },
    "category": "Beauty > Nail Care > Press-On Nails",
    "image": products[0]?.node.images.edges[0]?.node.url || "https://nailspresson.lovable.app/og-image.jpg",
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": Math.min(...products.map(p => parseFloat(p.node.priceRange.minVariantPrice.amount))).toFixed(2),
      "highPrice": Math.max(...products.map(p => parseFloat(p.node.priceRange.minVariantPrice.amount))).toFixed(2),
      "priceCurrency": products[0]?.node.priceRange.minVariantPrice.currencyCode || "GBP",
      "offerCount": products.length,
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "AI Press-On Nails UK"
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127",
      "bestRating": "5",
      "worstRating": "1"
    }
  } : null;

  // VideoObject Schema for tutorial content to enable video rich results
  // SiteNavigationElement Schema for sitelinks in search results
  const siteNavigationSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "SiteNavigationElement",
        "position": 1,
        "name": "Shop All Nails",
        "description": "Browse our complete collection of press-on nail designs",
        "url": "https://nailspresson.lovable.app/#products"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 2,
        "name": "AI Design Studio",
        "description": "Create custom nail art with AI technology",
        "url": "https://nailspresson.lovable.app/ai-design"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 3,
        "name": "Size Chart",
        "description": "Find your perfect press-on nail size",
        "url": "https://nailspresson.lovable.app/size-chart"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 4,
        "name": "FAQ",
        "description": "Frequently asked questions about press-on nails",
        "url": "https://nailspresson.lovable.app/faq"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 5,
        "name": "Contact Us",
        "description": "Get in touch with our customer support team",
        "url": "https://nailspresson.lovable.app/contact"
      }
    ]
  };

  // Review Schema for individual customer reviews
  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "AI Press-On Nails",
    "description": "Custom AI-designed press-on nails with unique nail art designs",
    "brand": {
      "@type": "Brand",
      "name": "AI Press-On Nails"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Sophie M."
        },
        "datePublished": "2024-11-15",
        "reviewBody": "Absolutely love these nails! The AI design feature is incredible - I described a galaxy theme and got exactly what I imagined. They lasted 2 weeks with no lifting.",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5",
          "worstRating": "1"
        }
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Emily R."
        },
        "datePublished": "2024-11-08",
        "reviewBody": "Perfect fit and beautiful quality. The size chart was super helpful. Will definitely order again for my wedding!",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5",
          "worstRating": "1"
        }
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Charlotte L."
        },
        "datePublished": "2024-10-29",
        "reviewBody": "Great alternative to salon visits. Easy to apply and remove. The French tips design was elegant and got so many compliments.",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5",
          "worstRating": "1"
        }
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Olivia T."
        },
        "datePublished": "2024-10-22",
        "reviewBody": "The custom AI design exceeded my expectations. Shipping was fast and packaging was lovely. Only giving 4 stars because one nail was slightly too big.",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "4",
          "bestRating": "5",
          "worstRating": "1"
        }
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Amelia K."
        },
        "datePublished": "2024-10-15",
        "reviewBody": "Game changer! I've tried so many press-ons and these are by far the best quality. The glitter ombre set is stunning.",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5",
          "worstRating": "1"
        }
      }
    ]
  };

  // Speakable Schema for voice search optimization
  const speakableSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "AI Press-On Nails UK - Custom Nail Art",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": [
        "h1",
        ".hero-description",
        ".product-title",
        ".faq-answer"
      ],
      "xpath": [
        "/html/head/title",
        "/html/head/meta[@name='description']/@content"
      ]
    },
    "url": "https://nailspresson.lovable.app"
  };

  // CollectionPage Schema for product category visibility
  const collectionPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Press-On Nails Collection",
    "description": "Browse our complete collection of AI-designed press-on nails. Custom nail art, premium quality, and unique designs for every style.",
    "url": "https://nailspresson.lovable.app/#products",
    "isPartOf": {
      "@type": "WebSite",
      "name": "AI Press-On Nails",
      "url": "https://nailspresson.lovable.app"
    },
    "about": {
      "@type": "Thing",
      "name": "Press-On Nails",
      "description": "Artificial nails that can be applied at home without salon visits"
    },
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": products.length,
      "itemListElement": products.slice(0, 12).map((product, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://nailspresson.lovable.app/product/${product.node.handle}`,
        "name": product.node.title
      }))
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://nailspresson.lovable.app/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Shop All",
          "item": "https://nailspresson.lovable.app/#products"
        }
      ]
    }
  };

  // SpecialAnnouncement Schema for promotions and sales
  const specialAnnouncementSchema = {
    "@context": "https://schema.org",
    "@type": "SpecialAnnouncement",
    "name": "Launch Sale - Premium Press-On Nails",
    "text": "Celebrate our launch with exclusive discounts on premium press-on nail sets. Join our waitlist for early access to new designs and special offers.",
    "datePosted": "2024-12-01T00:00:00+00:00",
    "expires": "2025-03-31T23:59:59+00:00",
    "category": "https://www.wikidata.org/wiki/Q27137228",
    "announcementLocation": {
      "@type": "WebSite",
      "name": "AI Press-On Nails",
      "url": "https://nailspresson.lovable.app"
    },
    "spatialCoverage": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "url": "https://nailspresson.lovable.app",
    "about": {
      "@type": "Event",
      "name": "Launch Sale Event",
      "description": "Limited-time launch promotion featuring discounts on all press-on nail collections",
      "startDate": "2024-12-01",
      "endDate": "2025-03-31",
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
      "organizer": {
        "@type": "Organization",
        "name": "AI Press-On Nails",
        "url": "https://nailspresson.lovable.app"
      },
      "offers": {
        "@type": "Offer",
        "name": "Launch Discount",
        "description": "Special launch pricing on premium press-on nail sets",
        "availability": "https://schema.org/InStock",
        "validFrom": "2024-12-01",
        "validThrough": "2025-03-31"
      }
    }
  };

  // WebApplication Schema for AI design tool
  const webApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "AI Nail Design Generator",
    "description": "Create custom press-on nail designs using artificial intelligence. Describe your vision or upload inspiration images to generate unique nail art.",
    "url": "https://nailspresson.lovable.app/ai-design",
    "applicationCategory": "DesignApplication",
    "operatingSystem": "Any",
    "browserRequirements": "Requires JavaScript. Requires HTML5.",
    "softwareVersion": "1.0",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "GBP",
      "description": "Free AI nail design generation"
    },
    "featureList": [
      "AI-powered nail art generation",
      "Text-to-image design creation",
      "Reference image upload",
      "Multiple style options",
      "Custom color selection",
      "Real-time preview"
    ],
    "screenshot": "https://nailspresson.lovable.app/og-image.jpg",
    "author": {
      "@type": "Organization",
      "name": "AI Press-On Nails",
      "url": "https://nailspresson.lovable.app"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "156",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  // Bestsellers ItemList Schema
  const bestsellersListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Bestselling Press-On Nail Collections",
    "description": "Our most popular press-on nail designs, handpicked by customers across the UK",
    "url": "https://nailspresson.lovable.app/#products",
    "numberOfItems": 6,
    "itemListOrder": "https://schema.org/ItemListOrderDescending",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Classic French Tips",
        "url": "https://nailspresson.lovable.app/product/classic-french-tips",
        "image": "https://nailspresson.lovable.app/products/classic-french-tips.jpg",
        "description": "Timeless elegance with our premium French tip press-on nails"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Pink Chrome",
        "url": "https://nailspresson.lovable.app/product/pink-chrome",
        "image": "https://nailspresson.lovable.app/products/pink-chrome.jpg",
        "description": "Stunning reflective pink chrome finish press-on nails"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Matte Black",
        "url": "https://nailspresson.lovable.app/product/matte-black",
        "image": "https://nailspresson.lovable.app/products/matte-black.jpg",
        "description": "Sophisticated matte black press-on nails for a bold look"
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "Floral Garden",
        "url": "https://nailspresson.lovable.app/product/floral-garden",
        "image": "https://nailspresson.lovable.app/products/floral-garden.jpg",
        "description": "Delicate floral designs on premium press-on nails"
      },
      {
        "@type": "ListItem",
        "position": 5,
        "name": "Glitter Ombre",
        "url": "https://nailspresson.lovable.app/product/glitter-ombre",
        "image": "https://nailspresson.lovable.app/products/glitter-ombre.jpg",
        "description": "Glamorous gradient glitter press-on nails"
      },
      {
        "@type": "ListItem",
        "position": 6,
        "name": "Nude Minimalist",
        "url": "https://nailspresson.lovable.app/product/nude-minimalist",
        "image": "https://nailspresson.lovable.app/products/nude-minimalist.jpg",
        "description": "Elegant nude tones for everyday sophistication"
      }
    ]
  };

  // Product Categories ItemList
  const categoriesListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Press-On Nail Categories",
    "description": "Browse our press-on nail collections by style and design",
    "url": "https://nailspresson.lovable.app",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "French Tips Collection",
        "url": "https://nailspresson.lovable.app/#products",
        "description": "Classic and modern French tip designs"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Chrome & Metallic Collection",
        "url": "https://nailspresson.lovable.app/#products",
        "description": "Reflective chrome and metallic finishes"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Artistic Designs Collection",
        "url": "https://nailspresson.lovable.app/#products",
        "description": "Unique floral, abstract, and artistic nail art"
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "Minimalist Collection",
        "url": "https://nailspresson.lovable.app/#products",
        "description": "Clean, simple, and elegant everyday designs"
      },
      {
        "@type": "ListItem",
        "position": 5,
        "name": "AI Custom Designs",
        "url": "https://nailspresson.lovable.app/ai-design",
        "description": "Create your own unique nail art with AI technology"
      }
    ]
  };

  // Service Schema for AI nail design service
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Custom AI Nail Design",
    "name": "AI Nail Art Design Service",
    "description": "Create unique, personalized press-on nail designs using artificial intelligence. Describe your vision and our AI generates custom nail art tailored to your style.",
    "url": "https://nailspresson.lovable.app/ai-design",
    "provider": {
      "@type": "Organization",
      "name": "AI Press-On Nails",
      "url": "https://nailspresson.lovable.app"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "AI Design Options",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Custom AI Design",
            "description": "Describe your dream nail design and let AI create it for you"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Reference Image Design",
            "description": "Upload inspiration images and AI will create a similar design"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Style-Based Design",
            "description": "Choose from themes like minimalist, glam, seasonal, or artistic"
          }
        }
      ]
    },
    "termsOfService": "https://nailspresson.lovable.app/terms",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "89",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": "How to Apply Press-On Nails - Complete Tutorial",
    "description": "Learn how to apply press-on nails at home for a salon-quality manicure. Step-by-step guide covering nail prep, sizing, application technique, and aftercare tips for nails that last up to 2 weeks.",
    "thumbnailUrl": "https://nailspresson.lovable.app/og-image.jpg",
    "uploadDate": "2024-01-15T08:00:00+00:00",
    "duration": "PT8M30S",
    "contentUrl": "https://www.youtube.com/watch?v=press-on-nails-tutorial",
    "embedUrl": "https://www.youtube.com/embed/press-on-nails-tutorial",
    "publisher": {
      "@type": "Organization",
      "name": "AI Press-On Nails",
      "logo": {
        "@type": "ImageObject",
        "url": "https://nailspresson.lovable.app/favicon.ico",
        "width": 60,
        "height": 60
      }
    },
    "interactionStatistic": {
      "@type": "InteractionCounter",
      "interactionType": { "@type": "WatchAction" },
      "userInteractionCount": 15420
    },
    "hasPart": [
      {
        "@type": "Clip",
        "name": "Nail Preparation",
        "startOffset": 0,
        "endOffset": 120,
        "url": "https://www.youtube.com/watch?v=press-on-nails-tutorial&t=0"
      },
      {
        "@type": "Clip",
        "name": "Sizing Your Nails",
        "startOffset": 120,
        "endOffset": 240,
        "url": "https://www.youtube.com/watch?v=press-on-nails-tutorial&t=120"
      },
      {
        "@type": "Clip",
        "name": "Application Technique",
        "startOffset": 240,
        "endOffset": 390,
        "url": "https://www.youtube.com/watch?v=press-on-nails-tutorial&t=240"
      },
      {
        "@type": "Clip",
        "name": "Aftercare Tips",
        "startOffset": 390,
        "endOffset": 510,
        "url": "https://www.youtube.com/watch?v=press-on-nails-tutorial&t=390"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>AI Press-On Nails | Custom Nail Art Designs UK | Free Shipping</title>
        <meta name="description" content="Create stunning custom press-on nails with AI technology. Unique nail art designs, premium quality, and free UK shipping. Design your perfect nails today!" />
        <meta name="keywords" content="press-on nails, custom nails, AI nail art, nail designs UK, fake nails, artificial nails" />
        <link rel="canonical" href="https://nailspresson.lovable.app/" />
        
        {/* Resource Hints for Performance */}
        <link rel="preconnect" href="https://cdn.shopify.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="https://cdn.shopify.com" />
        <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
        <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
        <link rel="dns-prefetch" href="https://www.google-analytics.com" />
        <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
        
        {/* Open Graph */}
        <meta property="og:title" content="AI Press-On Nails | Custom Nail Art Designs UK" />
        <meta property="og:description" content="Create stunning custom press-on nails with AI technology. Unique designs, premium quality." />
        <meta property="og:url" content="https://nailspresson.lovable.app/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://nailspresson.lovable.app/og-image.png" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Press-On Nails | Custom Nail Art Designs UK" />
        <meta name="twitter:description" content="Create stunning custom press-on nails with AI technology. Unique designs, premium quality." />
        <meta name="twitter:image" content="https://nailspresson.lovable.app/og-image.png" />
        
        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        {products.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify(itemListSchema)}
          </script>
        )}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
        {aggregateOfferSchema && (
          <script type="application/ld+json">
            {JSON.stringify(aggregateOfferSchema)}
          </script>
        )}
        <script type="application/ld+json">
          {JSON.stringify(videoSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(siteNavigationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(reviewSchema)}
        </script>
        {products.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify(collectionPageSchema)}
          </script>
        )}
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(specialAnnouncementSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webApplicationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(bestsellersListSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(categoriesListSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(speakableSchema)}
        </script>
      </Helmet>
      <Navigation />
      {/* Hero Section */}
      <section className="container py-8 sm:py-12 lg:py-20 relative px-4">
        <div className="absolute top-4 sm:top-10 right-4 sm:right-10 opacity-20 sm:opacity-30">
          <Heart className="h-8 w-8 sm:h-12 md:h-16 sm:w-12 md:w-16 text-primary animate-pulse" />
        </div>
        <div className="absolute bottom-10 sm:bottom-20 left-4 sm:left-10 opacity-20 sm:opacity-30">
          <Star className="h-6 w-6 sm:h-10 md:h-12 sm:w-10 md:w-12 text-accent animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--hero-gradient))] to-transparent rounded-full blur-3xl opacity-60 -z-10" />
            <div className="aspect-square rounded-2xl sm:rounded-3xl overflow-hidden bg-gradient-to-br from-[hsl(var(--product-pink))] to-[hsl(var(--product-lavender))] p-6 sm:p-8">
              <img 
                src={getOptimizedImageUrl(
                  products[0]?.node.images.edges[0]?.node.url || "https://images.unsplash.com/photo-1604654894610-df63bc536371",
                  600
                )}
                alt="Hero nail design" 
                className="w-full h-full object-contain drop-shadow-2xl"
                fetchPriority="high"
                loading="eager"
              />
            </div>
          </div>

          <div className="space-y-4 sm:space-y-6 text-left order-1 lg:order-2">
            <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-0 rounded-full px-3 sm:px-4 py-1 text-xs sm:text-sm">
              Your At-Home AI Nail Design
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight leading-tight">
              Bye-bye, Studiolook.
              <br />
              <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                Hello, Naturalness
              </span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl">
              Why does nail art always have to be such a nail salon thing? Can't it be done 
              without a nail designer, without annoying small talk and without dubious advice?
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" asChild className="rounded-full bg-gradient-to-r from-primary to-accent hover:opacity-90 h-12 sm:h-11 text-base sm:text-lg touch-manipulation">
                <Link to="/ai-design">
                  Create Your Design
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="gap-2 rounded-full border-2 h-12 sm:h-11 text-base sm:text-lg touch-manipulation">
                <Link to="/size-chart">
                  <Ruler className="h-4 w-4" />
                  Find Your Size
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="container py-12 sm:py-16 lg:py-20 px-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 sm:mb-12 gap-3">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">ALL PRESS-ON MANIS</h2>
          <a href="#" className="text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 group">
            Check all products
            <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {[...Array(8)].map((_, i) => (
              <Card key={i} className="overflow-hidden rounded-2xl sm:rounded-3xl">
                <div className="aspect-square bg-secondary/20 animate-pulse" />
                <CardContent className="p-3 sm:p-4 space-y-2">
                  <div className="h-4 bg-secondary/20 rounded animate-pulse" />
                  <div className="h-4 bg-secondary/20 rounded w-2/3 animate-pulse" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm sm:text-base text-muted-foreground mb-4">No products found</p>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Create a product by describing what you'd like to sell
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {products.slice(0, 8).map((product, idx) => {
              const bgColors = [
                'bg-[hsl(var(--product-pink))]',
                'bg-[hsl(var(--product-blue))]',
                'bg-[hsl(var(--product-mint))]',
                'bg-[hsl(var(--product-lavender))]',
                'bg-[hsl(var(--product-pink))]',
                'bg-[hsl(var(--product-blue))]',
                'bg-[hsl(var(--product-mint))]',
                'bg-[hsl(var(--product-lavender))]'
              ];
              
              return (
                <Card key={product.node.id} className="overflow-hidden group border-0 shadow-md hover:shadow-2xl transition-all duration-300 rounded-3xl relative">
                  <Badge className="absolute top-3 left-3 z-10 bg-foreground text-background rounded-md px-2 py-1 text-xs font-bold">
                    NEW
                  </Badge>
                  <Link to={`/product/${product.node.handle}`}>
                    <div className={`aspect-square overflow-hidden ${bgColors[idx % 8]} flex items-center justify-center p-8 relative`}>
                      {/* Decorative elements */}
                      {idx % 4 === 0 && (
                        <>
                          <Heart className="absolute top-4 right-4 h-6 w-6 text-primary/20" />
                          <Star className="absolute bottom-6 left-6 h-5 w-5 text-accent/20" />
                        </>
                      )}
                      {idx % 4 === 1 && (
                        <>
                          <Star className="absolute top-6 left-6 h-6 w-6 text-primary/20" />
                          <Heart className="absolute bottom-4 right-6 h-5 w-5 text-accent/20" />
                        </>
                      )}
                      {product.node.images.edges[0]?.node && (
                        <LazyImage
                          src={getOptimizedImageUrl(product.node.images.edges[0].node.url, 300)}
                          alt={product.node.images.edges[0].node.altText || product.node.title}
                          className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-lg"
                          containerClassName="w-full h-full"
                        />
                      )}
                    </div>
                  </Link>
                  <CardContent className="p-5 space-y-2 bg-background">
                    <Link to={`/product/${product.node.handle}`}>
                      <h3 className="font-bold text-base line-clamp-2 hover:text-primary transition-colors">
                        {product.node.title}
                      </h3>
                    </Link>
                    {product.node.options && product.node.options.length > 0 && product.node.options[0]?.name !== "Title" && (
                      <p className="text-xs text-muted-foreground">
                        {product.node.options.map(opt => opt.name).join(' • ')}
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-2">
                      <p className="text-sm font-bold text-foreground">
                        €{parseFloat(product.node.priceRange.minVariantPrice.amount).toFixed(2)}
                      </p>
                      <Button 
                        size="sm" 
                        onClick={() => handleAddToCart(product)}
                        className="rounded-full bg-primary hover:bg-primary/90 text-xs px-4"
                      >
                        ADD TO BAG
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Index;
